import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext.js';

type Mode = 'login' | 'register' | 'confirm' | 'forgot' | 'reset';

const MODE_HEADLINE: Record<Mode, string> = {
  login: "Welcome back — let's keep the momentum going.",
  register: "Let's get organized.",
  confirm: 'Check your email',
  forgot: "We'll email you a reset code.",
  reset: 'Enter your reset code and choose a new password.',
};

const MODE_BUTTON_LABEL: Record<Mode, string> = {
  login: 'Sign In',
  register: 'Create Account',
  confirm: 'Verify Email',
  forgot: 'Send Reset Code',
  reset: 'Reset Password',
};

export default function LoginPage() {
  const { signIn, signOut, signUp, confirmSignUp, forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await signIn(email, password);
        navigate('/');
      } else if (mode === 'register') {
        await signUp(email, password, displayName);
        setMode('confirm');
      } else if (mode === 'confirm') {
        await confirmSignUp(email, code);
        // A stale session left over in local storage (e.g. from a previous
        // sign-in in this browser that was never signed out of) makes
        // Amplify's signIn() throw "There is already a signed in user"
        // here, even though confirmSignUp above already succeeded — clear
        // it first so the newly-confirmed account actually gets signed in.
        await signOut().catch(() => {});
        await signIn(email, password);
        navigate('/');
      } else if (mode === 'forgot') {
        await forgotPassword(email);
        setMode('reset');
      } else if (mode === 'reset') {
        await resetPassword(email, code, newPassword);
        await signOut().catch(() => {});
        await signIn(email, newPassword);
        navigate('/');
      }
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <div className="auth-hero">
          <span className="auth-hero__logo">Ferngate</span>
          <h1 className="auth-hero__headline">
            Plan. Track. <span className="auth-hero__accent">Improve.</span>
          </h1>
          <p className="auth-hero__subhead">
            The to-do app that doesn&apos;t just track your tasks — it tells you how you&apos;re
            really doing, with AI-powered reports that celebrate progress and coach your next
            move.
          </p>

          <div className="auth-hero__features">
            <div className="auth-hero__feature">
              <span className="auth-hero__feature-label">Plan</span>
              <p>Organize tasks into lists, set priorities and due dates in seconds.</p>
            </div>
            <div className="auth-hero__feature">
              <span className="auth-hero__feature-label">Track</span>
              <p>See what&apos;s done, pending, and overdue at a glance.</p>
            </div>
            <div className="auth-hero__feature">
              <span className="auth-hero__feature-label">Improve</span>
              <p>Get a personalized AI report each week that celebrates wins and coaches your next move.</p>
            </div>
          </div>

          <p className="auth-hero__trust">
            Your data stays secure. Reports are generated via Claude AI.{' '}
            <Link to="/privacy" className="auth-hero__trust-link">
              Privacy Policy
            </Link>
          </p>
        </div>

          <div className="auth-card">
            <div className="auth-card__header">
              <p className="auth-card__tagline">{MODE_HEADLINE[mode]}</p>
            </div>

            <form className="auth-card__form" onSubmit={handleSubmit}>
              {error && <div className="auth-card__error">{error}</div>}

              {mode === 'confirm' ? (
                <div className="auth-card__field">
                  <label htmlFor="code">Verification Code</label>
                  <input
                    id="code"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter code from email"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </div>
              ) : mode === 'forgot' ? (
                <div className="auth-card__field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>
              ) : mode === 'reset' ? (
                <>
                  <div className="auth-card__field">
                    <label htmlFor="code">Reset Code</label>
                    <input
                      id="code"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter code from email"
                      autoComplete="one-time-code"
                      autoFocus
                      required
                    />
                  </div>
                  <div className="auth-card__field">
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  {mode === 'register' && (
                    <div className="auth-card__field">
                      <label htmlFor="displayName">Name</label>
                      <input
                        id="displayName"
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Your name"
                        autoComplete="name"
                        required
                      />
                    </div>
                  )}

                  <div className="auth-card__field">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      autoFocus={mode === 'login'}
                      required
                    />
                  </div>

                  <div className="auth-card__field">
                    <label htmlFor="password">Password</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                      required
                    />
                  </div>
                </>
              )}

              <button type="submit" className="btn btn--primary btn--full" disabled={loading}>
                {loading ? 'Please wait...' : MODE_BUTTON_LABEL[mode]}
              </button>

              {mode === 'register' && (
                <p className="auth-card__consent">
                  By creating an account, you acknowledge our{' '}
                  <Link to="/privacy">Privacy Policy</Link>.
                </p>
              )}
            </form>

            <div className="auth-card__footer">
              {mode === 'login' ? (
                <>
                  <button className="auth-card__link" onClick={() => setMode('register')}>
                    Create an account
                  </button>
                  <button className="auth-card__link" onClick={() => setMode('forgot')}>
                    Forgot password?
                  </button>
                </>
              ) : (
                <button className="auth-card__link" onClick={() => setMode('login')}>
                  Back to sign in
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}
